import React, { FC } from 'react';
import style from './MissingDataPage.scss';
import { Button, Heading, IncrementalInput, Text } from '../../liberis-component-library';
import { Business } from '../capitalAccountClient';
import { isUsa } from '../utils';

const containerId = 'container';

interface MissingDataInputProps {
  business: Business;
  availableOwnership: number;
  onSave: (ownership: number, ssn: string) => void;
}

const MissingDataInput: FC<MissingDataInputProps> = ({
  business,
  availableOwnership,
  onSave,
}): React.JSX.Element => {
  const [ssnError, setSsnError] = React.useState(false);
  const [ownership, setOwnership] = React.useState<number>(
    Math.min(
      business.signer.ownership ?? 0,
      availableOwnership,
    ),
  );
  const [ssn, setSsn] = React.useState<string>(business.signer.ssn ?? '');
  const ssnRegex = /^(?!000|666|9\d{2})\d{3}[- ]?(?!00)\d{2}[- ]?(?!0000)\d{4}$/;

  const onNewOwnership = ({ value }: { value: number }) => {
    setOwnership(value);
  };

  const onSsnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSsnError(false);
    let newSsn = e.target.value;

    // handle backspace at dash positions
    if (ssn.slice(-1) === '-'
        && newSsn === ssn.substring(0, ssn.length - 1)) {
      setSsn(ssn.substring(0, ssn.length - 2));
      return;
    }

    if (newSsn.startsWith('***-**-')) {
      newSsn = newSsn.slice(11); // remove the entire masked SSN
    }

    // strip out non-numeric characters
    newSsn = newSsn
      .replace(/\D/g, '') // remove non-numeric characters
      .slice(0, 9); // limit to 9 characters
    if (newSsn.length > 3) {
      newSsn = `${newSsn.slice(0, 3)}-${newSsn.slice(3)}`;
    }
    if (newSsn.length > 6) {
      newSsn = `${newSsn.slice(0, 6)}-${newSsn.slice(6)}`;
    }

    setSsn(newSsn);
  };

  const onSaveClick = () => {
    if (Number.isNaN(ownership)
        || ownership < 0
        || ownership > availableOwnership
        || (isUsa(business)
          && (ssn.length !== 11
            || !(ssn.startsWith('***-**-') || ssnRegex.test(ssn))))) { // format: 000-00-0000
      setSsnError(true);
      return;
    }

    if (!isUsa(business) || ssn.startsWith('***-**-')) {
      // If USA or the SSN is masked, we don't save it
      onSave(ownership, undefined);
    } else {
      onSave(ownership, ssn.replace(/-/g, ''));
    }
  };

  return (
    <main id={containerId} className={style.container}>
      <div className={style.content}>
        <Heading heading='h2'>Add your details</Heading>
        <Text className={style.subTitle}>
          You have been invited as a stakeholder in {business.name} and are required to provide your details in order to
          obtain business finance.
        </Text>
        <div className={style.contentContainer}>
          <Heading heading='h3'>Business ownership and Social Security Number</Heading>
          <Text>% share of the business</Text>
          <IncrementalInput
            className={style.incrementalInput}
            value={ownership}
            onChange={onNewOwnership}
            min={0}
            max={availableOwnership}
          />
          {isUsa(business) && (
            <>
              <Text>Social security number</Text>
              <Text className={ssnError ? style.ssnError : style.ssnErrorHidden}>
                Please enter a valid SSN in the format 123-45-6789
              </Text>
              <input value={ssn} onChange={onSsnChange} className={`${style.ssnInput} ${ssnError ? style.inputError : ''}`} placeholder='123-45-6789' />
            </>
          )}
          <Button className={style.button} onClick={onSaveClick}>
            Save details
          </Button>
        </div>
      </div>
    </main>
  );

};

export default MissingDataInput;
